import React, { useEffect, useState } from "react";
import { useAuthState, useAuthCurator } from "@src/hooks/useAuth";
import CuratorInvoicePage from "./CuratorInvoicePage";
import AdminInvoicePage from "./AdminInvoicePage";
import WrapperContainer from "@src/components/WrapperContainer/WrapperContainer";
import PageTopSection from "@src/components/PageTopSection/PageTopSection";
import PageTitle from "@src/components/PageTitle/PageTitle";
import ContentContainer from "@src/components/ContentContainer/ContentContainer";
import {
  PlaylistOverviewHeader,
  PlaylistOverviewHeaderLeft,
  PlaylistOverviewHeaderRight,
  ButtonSection,
  BottomButtonsContainer,
  BottomButtons,
  AddActionButton,
  SearchContainer,
  SearchInputWrapper,
  TotalLabelWrapper,
  TotalLabel,
} from "./Invoices.styles";
import { SearchInput } from "@src/components/SearchInput";
import ContentDrawer from "@src/components/ContentDrawer";
import { useForm, Controller } from "react-hook-form";
import {
  useInfiniteInvoices,
  useInfiniteMyInvoices,
} from "@src/queries/invoiceQueries";

const InvoicesPage = (): React.JSX.Element => {
  const { role } = useAuthState();
  const curator = useAuthCurator();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [searchText, setSearchText] = useState("");

  const isAdminOrManager = role?.type === "admin" || role?.type === "manager";

  const { control, watch } = useForm({
    defaultValues: {
      searchText: "",
    },
  });

  const watchSearchText = watch("searchText");

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchText(watchSearchText || "");
    }, 500);
    return () => clearTimeout(handler);
  }, [watchSearchText]);

  const allInvoices = useInfiniteInvoices({ enabled: isAdminOrManager });
  const myInvoices = useInfiniteMyInvoices(curator?.documentId, {
    enabled: !isAdminOrManager && !!curator?.documentId,
  });

  const currentInvoices = isAdminOrManager ? allInvoices : myInvoices;
  const totalCount =
    currentInvoices.data?.pages?.[0]?.data?.meta?.pagination?.total || 0;

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
  };

  return (
    <WrapperContainer noScroll>
      <PageTopSection>
        <PlaylistOverviewHeader>
          <PlaylistOverviewHeaderLeft>
            <ButtonSection>
              <PageTitle>Invoices</PageTitle>
              {!isAdminOrManager && (
                <BottomButtonsContainer>
                  <BottomButtons>
                    <AddActionButton $active={true} onClick={toggleDrawer}>
                      + Invoice
                    </AddActionButton>
                  </BottomButtons>
                </BottomButtonsContainer>
              )}
            </ButtonSection>
          </PlaylistOverviewHeaderLeft>
          <PlaylistOverviewHeaderRight>
            <SearchContainer>
              <SearchInputWrapper>
                <Controller
                  name="searchText"
                  control={control}
                  render={({ field }) => (
                    <SearchInput {...field} placeholder="Search by name" />
                  )}
                />
              </SearchInputWrapper>
              <TotalLabelWrapper>
                <TotalLabel>Total: {totalCount}</TotalLabel>
              </TotalLabelWrapper>
            </SearchContainer>
          </PlaylistOverviewHeaderRight>
        </PlaylistOverviewHeader>
      </PageTopSection>
      <ContentContainer allowScroll={true}>
        <ContentDrawer
          $floatingDrawer
          isOpen={isDrawerOpen}
          toggleDrawer={handleCloseDrawer}
        >
          {isDrawerOpen && (
            <CuratorInvoicePage onToggleView={handleCloseDrawer} />
          )}
        </ContentDrawer>
        <AdminInvoicePage searchText={searchText} />
      </ContentContainer>
    </WrapperContainer>
  );
};

export default InvoicesPage;
